import React, { useState, useEffect } from 'react';
import { CheckCircle, Key, Calendar, Clock, MessageCircle, Shield, DollarSign, ShoppingCart, RefreshCw, Download, Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import BackupManager from '../Backup/BackupManager';

const SubscriptionInfo = () => {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [showBackupManager, setShowBackupManager] = useState(false);
  const [unitValue, setUnitValue] = useState('');
  const [editingUnit, setEditingUnit] = useState(true); // Start as true for new users
  const [unitSystemEnabled, setUnitSystemEnabled] = useState(false); // New state for toggle
  
  // Estados para modal de zerar dados
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  
  // Estados para atualização do PWA
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [currentVersion, setCurrentVersion] = useState('v2.8.2');

  useEffect(() => {
    if (user) {
      setSubscriptionData({
        accessKey: user.access_key || 'STK-XXXXXXXXXX',
        expirationDate: user.key_expires_at || new Date(),
        name: user.name || 'Usuário',
        email: user.email || 'usuario@email.com',
        memberSince: user.created_at || new Date(),
        status: 'active' // This would come from the API
      });
      // Only update unitValue from user if not currently editing
      if (!editingUnit) {
        setUnitValue(user.unit_value || '');
      } else {
        // If starting fresh, set the value and check if should be in edit mode
        setUnitValue(user.unit_value || '');
        setEditingUnit(!user.unit_value); // Only in edit mode if no value set
      }
      // Set toggle based on whether unit_value exists and is not null
      // If user has no value, keep system "enabled" so they can configure
      if (user.unit_value !== null && user.unit_value !== undefined && user.unit_value > 0) {
        setUnitSystemEnabled(true);
      } else {
        setUnitSystemEnabled(true); // Start enabled for new users to configure
      }
    }
  }, [user]);

  const calculateDaysRemaining = () => {
    if (!subscriptionData) return 0;
    const now = new Date();
    const expiration = new Date(subscriptionData.expirationDate);
    const diffTime = expiration.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusColor = () => {
    const daysRemaining = calculateDaysRemaining();
    if (daysRemaining > 7) return 'text-green-600 dark:text-green-400';
    if (daysRemaining > 3) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const handleSaveUnitValue = async () => {
    try {
      setLoading(true);
      const value = unitValue ? parseFloat(unitValue) : null;
      
      if (!user?.id) {
        throw new Error('ID do usuário não encontrado');
      }
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}/unit-value`;
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ unit_value: value })
      });

      if (response.ok) {
        const updatedUser = await response.json();
        // Update user in context and localStorage
        const newUserData = { ...user, unit_value: value };
        setUser(newUserData);
        localStorage.setItem('stakemind_user', JSON.stringify(newUserData));
        setEditingUnit(false);
        alert('✅ Valor da unidade salvo com sucesso!');
      } else {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error('Falha ao salvar');
      }
    } catch (error) {
      console.error('💥 Error saving unit value:', error);
      alert('❌ Erro ao salvar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClearUnitValue = async () => {
    if (!confirm('Tem certeza que deseja remover o valor da unidade? Isso voltará ao modo tradicional de stake.')) return;
    
    setUnitValue('');
    await handleSaveUnitValue();
  };

  const handleToggleUnitSystem = async () => {
    try {
      setLoading(true);
      
      if (!user?.id) {
        throw new Error('ID do usuário não encontrado');
      }
      
      // Get current value from user
      const currentValue = user.unit_value;
      
      if (!currentValue || parseFloat(currentValue) <= 0) {
        alert('⚠️ Erro: Nenhum valor configurado para alternar.');
        setLoading(false);
        return;
      }
      
      // If currently enabled, set to null (pause)
      // If currently disabled, restore the value from unitValue state
      const newValue = unitSystemEnabled ? null : parseFloat(currentValue);
      
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}/unit-value`;
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ unit_value: newValue })
      });

      if (response.ok) {
        const newUserData = { ...user, unit_value: newValue };
        setUser(newUserData);
        localStorage.setItem('stakemind_user', JSON.stringify(newUserData));
        setUnitSystemEnabled(!unitSystemEnabled);
        
        if (unitSystemEnabled) {
          alert('⏸️ Sistema pausado. Valor mantido mas modo de unidades desativado.');
        } else {
          alert('▶️ Sistema reativado! Modo de unidades habilitado novamente.');
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error('Falha ao alternar sistema');
      }
    } catch (error) {
      console.error('💥 Error toggling unit system:', error);
      alert('❌ Erro ao alternar sistema. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Função para verificar atualizações do PWA
  const checkAndUpdateServiceWorker = async () => {
    setCheckingUpdate(true);
    
    try {
      console.log('🔍 [iOS Fix] Verificando atualizações do app...');
      
      // Detecta se é iOS
      const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) || 
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      
      console.log(`📱 Plataforma detectada: ${isIOS ? 'iOS' : 'Outros'}`);
      
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        
        if (registration) {
          console.log('🔄 Forçando atualização do Service Worker...');
          
          // Para iOS: Método mais agressivo
          if (isIOS) {
            console.log('📱 Aplicando estratégia iOS...');
            
            // 1. Unregister o service worker atual
            await registration.unregister();
            console.log('✅ Service Worker removido');
            
            // 2. Limpa TODOS os caches
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => {
              console.log(`🗑️ Removendo cache: ${name}`);
              return caches.delete(name);
            }));
            console.log('✅ Todos os caches limpos');
            
            // 3. Aguarda um pouco
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // 4. Registra novamente
            console.log('🔄 Re-registrando Service Worker...');
            const newReg = await navigator.serviceWorker.register('/service-worker.js', {
              scope: '/',
              updateViaCache: 'none'
            });
            
            console.log('✅ Service Worker re-registrado');
            
            // 5. Aguarda instalação
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // 6. Recarrega a página
            console.log('🔄 Recarregando app com nova versão...');
            alert('✅ App atualizado! A página será recarregada.');
            window.location.reload(true); // true = hard reload
            
          } else {
            // Para outros navegadores: Método padrão melhorado
            console.log('💻 Aplicando estratégia padrão...');
            
            // Força atualização
            await registration.update();
            
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            if (registration.waiting) {
              console.log('✅ Nova versão encontrada, aplicando...');
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
              
              // Aguarda controllerchange
              await new Promise(resolve => {
                navigator.serviceWorker.addEventListener('controllerchange', resolve, { once: true });
                setTimeout(resolve, 3000); // Timeout de segurança
              });
              
              alert('✅ App atualizado! A página será recarregada.');
              window.location.reload();
            } else if (registration.installing) {
              console.log('⏳ Nova versão instalando...');
              setUpdateAvailable(true);
              alert('🔄 Nova versão detectada! Instalando...');
            } else {
              console.log('✅ App já está na versão mais recente');
              
              // Mesmo assim, limpa o cache e recarrega para garantir
              const cacheNames = await caches.keys();
              await Promise.all(cacheNames.map(name => caches.delete(name)));
              
              alert('✅ Cache limpo! App será recarregado para garantir a versão mais recente.');
              window.location.reload(true);
            }
          }
        } else {
          console.log('⚠️ Service Worker não registrado, tentando registrar...');
          await navigator.serviceWorker.register('/service-worker.js', {
            scope: '/',
            updateViaCache: 'none'
          });
          alert('✅ Service Worker registrado! Recarregue a página.');
          window.location.reload();
        }
      } else {
        alert('⚠️ Seu navegador não suporta Service Worker.');
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar:', error);
      
      // Fallback: Mesmo com erro, tenta limpar cache e recarregar
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        alert('⚠️ Houve um problema, mas o cache foi limpo. Recarregando...');
        window.location.reload(true);
      } catch (e) {
        alert('❌ Erro ao forçar atualização. Tente fechar e reabrir o app.');
      }
    } finally {
      setCheckingUpdate(false);
    }
  };

  const handleResetAllData = async () => {
    if (resetConfirmText !== 'ZERAR TUDO') {
      alert('❌ Por favor, digite "ZERAR TUDO" para confirmar');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}/reset-data`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.ok) {
        const data = await response.json();
        const deletedSummary = `
🗑️ Seus Dados Foram Zerados com Sucesso!

• ${data.deleted.bets} apostas excluídas
• ${data.deleted.betting_houses} casas de apostas excluídas
• ${data.deleted.groups} grupos excluídos
• ${data.deleted.expenses} despesas excluídas
• ${data.deleted.goals} metas excluídas  
• ${data.deleted.daily_records} registros diários excluídos

Você pode começar do zero agora! 🎯
        `;
        alert(deletedSummary);
        setShowResetModal(false);
        setResetConfirmText('');
        
        // Recarregar a página para atualizar todos os dados
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Falha ao zerar dados');
      }
    } catch (error) {
      console.error('Erro ao zerar dados:', error);
      alert(`❌ Erro ao zerar dados: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };


  const features = [
    'Gestão completa de bancas',
    'Registro ilimitado de apostas',
    'Relatórios detalhados',
    'Gráficos interativos',
    'Histórico completo',
    'Suporte técnico',
    'Atualizações automáticas',
    'Interface responsiva',
    'Modo escuro/claro'
  ];

  if (!subscriptionData) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando informações...</p>
        </div>
      </div>
    );
  }

  const daysRemaining = calculateDaysRemaining();

  return (
    <div className="space-y-6">
      {/* Modal de Confirmação para Zerar Dados */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border-2 border-red-500 max-w-lg w-full p-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-10 h-10 text-red-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                ⚠️ ATENÇÃO: Ação Irreversível!
              </h3>
              <p className="text-red-400 font-semibold mb-4">
                Esta ação irá EXCLUIR PERMANENTEMENTE todos os seus dados:
              </p>
            </div>

            <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
              <ul className="space-y-2 text-sm text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span>Todas as apostas registradas</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span>Todas as casas de apostas</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span>Todos os grupos de apostas</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span>Todas as despesas</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span>Todas as metas de lucro</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 font-bold">•</span>
                  <span>Todos os registros diários</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-300">
                <strong>⚠️ IMPORTANTE:</strong> Esta ação NÃO pode ser desfeita! Todos os dados serão perdidos permanentemente. Sua assinatura permanecerá ativa.
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-white mb-2">
                Para confirmar, digite exatamente: <span className="text-red-400">ZERAR TUDO</span>
              </label>
              <input
                type="text"
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="Digite ZERAR TUDO"
                className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg border-2 border-gray-600 focus:border-red-500 focus:outline-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowResetModal(false);
                  setResetConfirmText('');
                }}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleResetAllData}
                disabled={loading || resetConfirmText !== 'ZERAR TUDO'}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Zerando...' : 'Confirmar e Zerar Tudo'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Minha Assinatura
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Informações sobre sua chave de acesso
          </p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subscription Status */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Status da Assinatura</h3>
            <CheckCircle className="w-6 h-6 text-green-500" />
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <Key className="w-5 h-5 text-blue-500" />
                <span className="text-gray-700 dark:text-gray-300">Chave de Acesso</span>
              </div>
              <span className="font-mono text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-3 py-1 rounded">
                {subscriptionData.accessKey}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <Calendar className="w-5 h-5 text-purple-500" />
                <span className="text-gray-700 dark:text-gray-300">Data de Expiração</span>
              </div>
              <span className="font-medium text-gray-900 dark:text-white">
                {formatDate(subscriptionData.expirationDate)}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-orange-500" />
                <span className="text-gray-700 dark:text-gray-300">Tempo Restante</span>
              </div>
              <span className={`font-medium ${getStatusColor()}`}>
                {daysRemaining > 0 ? `${daysRemaining} dias` : 'Expirado'}
              </span>
            </div>
          </div>
        </div>

        {/* Account Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Informações da Conta</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Nome</label>
              <p className="text-gray-900 dark:text-white font-medium">{subscriptionData.name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">E-mail</label>
              <p className="text-gray-900 dark:text-white font-medium">{subscriptionData.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Membro desde</label>
              <p className="text-gray-900 dark:text-white font-medium">
                {formatDate(subscriptionData.memberSince)}
              </p>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <a
              href="/checkout"
              className="w-full bg-gradient-to-r from-green-600 to-teal-600 text-white py-3 rounded-lg font-medium hover:from-green-700 hover:to-teal-700 transition-colors inline-flex items-center justify-center"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Renovar Assinatura
            </a>
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
              Renovação automática via checkout
            </p>
          </div>
        </div>

        {/* Unit Value Configuration */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Configuração de Unidade
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Para apostadores que trabalham com unidades
                </p>
              </div>
            </div>
            
            {/* Toggle Switch - Only show if user has a value configured */}
            {user?.unit_value && parseFloat(user.unit_value) > 0 && (
              <div className="flex items-center space-x-3">
                <span className={`text-sm font-medium ${unitSystemEnabled ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {unitSystemEnabled ? '✅ Ativo' : '⚪ Pausado'}
                </span>
                <button
                  onClick={handleToggleUnitSystem}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                    unitSystemEnabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                  title={unitSystemEnabled ? 'Clique para pausar o sistema de unidades' : 'Clique para reativar o sistema de unidades'}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      unitSystemEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            )}
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <strong>ℹ️ Como funciona:</strong><br/>
              Configure o valor em reais de 1 unidade. Ao registrar apostas, você poderá informar quantas unidades (1u, 1.5u, 2.8u, etc.) e o sistema calculará automaticamente o stake em R$.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Valor de 1 Unidade (R$)
              </label>
              <div className="flex gap-3">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={unitValue}
                  onChange={(e) => setUnitValue(e.target.value)}
                  placeholder="Ex: 50.00"
                  disabled={!editingUnit && unitValue}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-600"
                />
                {!editingUnit && unitValue ? (
                  <button
                    onClick={() => setEditingUnit(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Editar
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSaveUnitValue}
                      disabled={loading}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Salvando...' : 'Salvar'}
                    </button>
                    {unitValue && (
                      <button
                        onClick={handleClearUnitValue}
                        disabled={loading}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Limpar
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {unitValue && parseFloat(unitValue) > 0 && unitSystemEnabled && (
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>✅ Sistema Ativo:</strong> 1 unidade = R$ {parseFloat(unitValue).toFixed(2)}<br/>
                  <strong>Exemplo:</strong> 1.5u = R$ {(parseFloat(unitValue) * 1.5).toFixed(2)}<br/>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Ao registrar apostas, use o modo "Unidades" para apostar em múltiplos da unidade configurada.
                  </span>
                </p>
              </div>
            )}

            {user?.unit_value && parseFloat(user.unit_value) > 0 && !unitSystemEnabled && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>⏸️ Sistema Pausado:</strong> Valor configurado (R$ {parseFloat(user.unit_value).toFixed(2)}) mas modo de unidades está desativado.<br/>
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Use o botão acima para reativar quando quiser voltar a usar unidades.
                  </span>
                </p>
              </div>
            )}

            {!unitValue && !user?.unit_value && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>ℹ️ Modo Tradicional Ativo</strong><br/>
                  Configure um valor acima e clique em "Salvar" para começar a usar o sistema de unidades.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Recursos Inclusos</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature, index) => (
            <div key={index} className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-gray-700 dark:text-gray-300">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Expiration Warning */}
      {daysRemaining <= 7 && daysRemaining > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Clock className="w-6 h-6 text-yellow-600" />
            <div>
              <h4 className="font-medium text-yellow-800 dark:text-yellow-200">
                ⚠️ Sua assinatura expira em breve
              </h4>
              <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
                Sua assinatura expira em {daysRemaining} dia{daysRemaining !== 1 ? 's' : ''}. 
                Renove agora para não perder o acesso aos recursos.
              </p>
              <a
                href="/checkout"
                className="inline-flex items-center space-x-2 mt-3 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors text-sm"
              >
                <ShoppingCart className="w-4 h-4" />
                <span>Renovar Agora</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Expired Warning */}
      {daysRemaining <= 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-red-600" />
            <div>
              <h4 className="font-medium text-red-800 dark:text-red-200">
                🚨 Assinatura Expirada
              </h4>
              <p className="text-red-700 dark:text-red-300 text-sm mt-1">
                Sua licença expirou. Renove agora para continuar usando o sistema.
              </p>
              <a
                href="/checkout"
                className="inline-flex items-center space-x-2 mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                <ShoppingCart className="w-4 h-4" />
                <span>Renovar Agora</span>
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Backup Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Backup dos Dados
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Mantenha seus dados seguros
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            💾 Faça backup de todas as suas apostas, despesas, grupos e configurações.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            🔄 Sistema de lembretes automáticos para backups regulares.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            📤 Restaure seus dados a qualquer momento.
          </p>
        </div>
        
        <button
          onClick={() => setShowBackupManager(true)}
          className="w-full mt-4 flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
        >
          <Shield className="w-5 h-5" />
          <span>Gerenciar Backup</span>
        </button>
      </div>

      {/* App Update Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex items-start space-x-3 mb-4">
          <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg">
            <RefreshCw className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              📱 Atualização do App
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Versão atual: <span className="font-semibold text-purple-600 dark:text-purple-400">{currentVersion}</span>
            </p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            💡 Mantenha seu app sempre atualizado com as últimas funcionalidades e correções.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            🔄 O sistema verifica automaticamente a cada 10 minutos, mas você pode forçar a verificação aqui.
          </p>
        </div>

        {updateAvailable && (
          <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/30 border border-green-500 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Download className="w-5 h-5 text-green-600 dark:text-green-400" />
              <p className="text-sm font-semibold text-green-700 dark:text-green-300">
                ✨ Nova versão disponível!
              </p>
            </div>
            <p className="text-xs text-green-600 dark:text-green-400">
              Clique em "Atualizar Agora" para aplicar a nova versão.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <button
            onClick={checkAndUpdateServiceWorker}
            disabled={checkingUpdate}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed font-bold shadow-lg"
          >
            <RefreshCw className={`w-5 h-5 ${checkingUpdate ? 'animate-spin' : ''}`} />
            <span>{checkingUpdate ? 'Atualizando...' : '🔄 Forçar Atualização Completa'}</span>
          </button>
        </div>

        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            <strong>📱 Especial para iOS:</strong> Este botão força uma atualização completa,
            limpando todo o cache e reinstalando o app. Ideal para garantir que você está sempre
            na versão mais recente!
          </p>
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
          💡 O app será recarregado automaticamente após a atualização
        </p>
      </div>

      {/* Zona de Perigo - Zerar Dados */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 border-red-500 p-6">
        <div className="flex items-start space-x-3 mb-4">
          <div className="bg-red-100 dark:bg-red-900/30 p-2 rounded-lg">
            <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              ⚠️ Zona de Perigo
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Ações irreversíveis - Use com extremo cuidado!
            </p>
          </div>
        </div>

        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <p className="text-sm text-red-700 dark:text-red-300 mb-2">
            <strong>Zerar Todos os Dados:</strong> Remove permanentemente todas as suas apostas, casas de apostas, grupos, despesas, metas e registros.
          </p>
          <p className="text-xs text-red-600 dark:text-red-400">
            ⚠️ Esta ação NÃO pode ser desfeita! Recomendamos fazer um backup antes de prosseguir.
          </p>
        </div>

        <button
          onClick={() => setShowResetModal(true)}
          className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
        >
          <Trash2 className="w-5 h-5" />
          <span>Zerar Todos os Dados</span>
        </button>
      </div>

      {/* Backup Manager Modal */}
      {showBackupManager && (
        <BackupManager
          user={user}
          onClose={() => setShowBackupManager(false)}
        />
      )}
    </div>
  );
};

export default SubscriptionInfo;